/**
 * WHICH HOUSEHOLD, when a form needs one and nobody knows her id.
 *
 * A select over the client book is a control that works for eleven households and for nothing
 * after that, and the client book is the one thing this business is trying to grow. So this is a
 * search box: type two letters, press a name, and the name stays on the screen with a way to
 * change it. `GET /api/clients?q=` is the same read the client book itself makes.
 *
 * TYPING IS NOT A REQUEST. A quarter second of quiet is, the same debounce `screens/ClientList.tsx`
 * uses, because this is typed one-handed on mobile data and a request per keystroke is a request
 * per keystroke.
 *
 * NOTHING CHOSEN IS A REAL ANSWER and the field says so. Most follow-ups in this business are about
 * no household in particular, and a picker that insists on one is a picker somebody puts the
 * nearest name into.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { listClients, type ClientRow } from '../api.ts';
import { Field } from './Field.tsx';

/** How many names a search offers. Past this the answer is to type another letter. */
const SHOWN = 6;

export function HouseholdPicker({
  idPrefix,
  label,
  hint,
  clientId,
  onChange,
}: {
  idPrefix: string;
  label: string;
  hint?: ReactNode;
  /** The id chosen, or the empty string for none, which the caller sends as nothing at all. */
  clientId: string;
  onChange: (clientId: string) => void;
}) {
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<readonly ClientRow[]>([]);
  const [chosen, setChosen] = useState<ClientRow | null>(null);
  const id = `${idPrefix}-household`;

  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 250);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    if (query === '' || chosen) {
      setFound([]);
      return;
    }
    let live = true;
    listClients(query)
      .then((answer) => {
        if (live) setFound(answer.clients.slice(0, SHOWN));
      })
      .catch(() => {
        // A search that did not answer offers nothing rather than an error beside a box that is
        // optional anyway. The form still saves, without a household on it.
        if (live) setFound([]);
      });
    return () => {
      live = false;
    };
  }, [query, chosen]);

  if (chosen && clientId !== '') {
    return (
      <Field id={id} label={label}>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="font-serif text-base">{chosen.displayName}</span>
          <button
            type="button"
            onClick={() => {
              setChosen(null);
              onChange('');
              setTyped('');
            }}
            className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 font-medium"
          >
            <span className="text-sm">Choose another</span>
          </button>
        </div>
      </Field>
    );
  }

  return (
    <Field id={id} label={label} hint={hint}>
      <input
        id={id}
        type="search"
        value={typed}
        autoComplete="off"
        placeholder="Type a name"
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => setTyped(event.target.value)}
        className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
      />
      {query !== '' && found.length === 0 ? (
        <p className="mt-1 text-xs text-navy/65">No household answers to that.</p>
      ) : null}
      {found.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {found.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => {
                  setChosen(client);
                  onChange(client.id);
                }}
                className="flex min-h-11 desk:min-h-9 w-full items-center rounded-lg border border-navy/20 bg-parchment px-3 text-left font-serif"
              >
                <span className="truncate text-base">{client.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Field>
  );
}
